import { useNavigate, useParams } from 'react-router';
import { useEffect, useMemo, useState } from 'react';
import { IDisclosure, IParticipatingOrganizationDto } from '@vivli/features/disclosures/infrastructure/interface';
import { DisclosureStatusEnum } from '@vivli/features/disclosures/infrastructure/enum';
import { DisclosureView } from '@vivli/features/disclosures/views';
import {
    useActiveUserContext,
    useAppConfigContext,
    useAuthServiceContext,
    useDataRequestsApiContext,
    useDisclosuresApiContext
} from '@vivli/core/infrastructure/context';
import { LoadIndicatorCenteredView } from '@vivli/shared/views';
import { finalize, first, map, switchMap } from 'rxjs/operators';
import { IPageLink } from '@vivli/shared/infrastructure/interface';
import { IDataRequest } from '@vivli/features/data-requests/infrastructure/interface';
import { forkJoin, Observable } from 'rxjs';
import { CitationsContextWrapper } from '@vivli/shared/features/citations/infrastructure/wrappers';
import { useDataPackage } from '@vivli/shared/infrastructure/hook';
import { useOrganizations } from '@vivli/features/organizations/infrastructure/hook';
import { IOrganization } from '@vivli/features/organizations/infrastructure/interface';
import { NotFoundComponent } from '@vivli/shared/components';
import { LoggerService } from '@vivli/shared/infrastructure/service';

interface DisclosureBaseFeatureProps {
    withVivliId: boolean;
}

export const DisclosureBaseFeature = ({ withVivliId }: DisclosureBaseFeatureProps) => {
    const { disclosureId, dataRequestId, tab, vivliId } = useParams();

    const { user } = useActiveUserContext();
    const { organizations } = useOrganizations();

    const [disclosure, setDisclosure] = useState<IDisclosure>();
    const [dataRequest, setDataRequest] = useState<IDataRequest>();
    const [isLoading, setIsLoading] = useState<boolean>();
    const [notFound, setNotFound] = useState(false); //for use in url error checking

    const reviewDocsPackage = useDataPackage({ dataPackageId: disclosure?.disclosureDocumentPackageId, fileLimit: 2 });
    const attachmentsPackage = useDataPackage({ dataPackageId: disclosure?.disclosureAttachmentPackageId });

    const _navigate = useNavigate();
    const _disclosureService = useDisclosuresApiContext();
    const _dataRequestsService = useDataRequestsApiContext();
    const _config = useAppConfigContext();
    const _auth = useAuthServiceContext();

    const updateDisclosure = (updatedDisclosure: IDisclosure) => {
        const disclosureWithHistories = {
            ...updatedDisclosure,
            disclosureHistories: updatedDisclosure.disclosureHistories.map((history) => {
                const thisOrg: IOrganization = organizations?.find((o) => o.id === history.organizationId);
                return { ...history, organizationName: thisOrg?.name || '' };
            })
        };
        setDisclosure(disclosureWithHistories);
        return disclosureWithHistories;
    };

    const handleLinkClick = (link: IPageLink, id?: number) => {
        if (withVivliId && vivliId) {
            _navigate(`/disclosure/vivliId/${vivliId}/${link.url}`, { replace: true });
            return;
        }

        _navigate(`/disclosure/${dataRequestId}/${id}/${link.url}`, { replace: true });
    };

    const handleSave = (updatedDisclosure: IDisclosure): Observable<IDisclosure> => {
        setIsLoading(true);
        return _disclosureService.saveDisclosure(updatedDisclosure).pipe(
            first(),
            map(updateDisclosure),
            finalize(() => {
                setIsLoading(false);
            })
        );
    };

    const handleSubmit = (updatedDisclosure: IDisclosure) => {
        setIsLoading(true);
        return _disclosureService.saveDisclosure(updatedDisclosure).pipe(
            first(),
            switchMap((savedDisclosure) => {
                return _disclosureService.changeDisclosureStatus(savedDisclosure, DisclosureStatusEnum.ThirtyDayReview);
            }),
            map(updateDisclosure),
            finalize(() => {
                setIsLoading(false);
            })
        );
    };

    const handleReviewFinalized = (organization: IParticipatingOrganizationDto): Observable<IDisclosure> => {
        setIsLoading(true);
        return _disclosureService.finalizeDisclosure(disclosureId, organization).pipe(
            first(),
            map(updateDisclosure),
            finalize(() => setIsLoading(false))
        );
    };

    const handleChangeStatus = (updatedDisclosure: IDisclosure, status: DisclosureStatusEnum) => {
        setIsLoading(true);
        return _disclosureService.changeDisclosureStatus(updatedDisclosure, status).pipe(
            first(),
            map(updateDisclosure),
            finalize(() => setIsLoading(false))
        );
    };

    const initializeData = () => {
        if (vivliId && !disclosureId && !dataRequest) {
            _disclosureService.getDisclosureByVivliId(vivliId).subscribe({
                next: (loadedDisclosure) => {
                    setDisclosure(loadedDisclosure);
                    LoggerService.log(`VivliId provided without disclosureId, found disclosure with id ${loadedDisclosure.id}.`);
                    const requestId = loadedDisclosure.dataRequestId;
                    _dataRequestsService.getDataRequest(requestId).subscribe({
                        next: (loadedDataRequest) => {
                            setDataRequest(loadedDataRequest);
                        },
                        error: (error) => {
                            setNotFound(true);
                            setIsLoading(false);
                        }
                    });
                },
                error: (error) => {
                    setNotFound(true);
                    setIsLoading(false);
                }
            });
        } else if (disclosureId) {
            const dataRequestApiCall = _dataRequestsService.getDataRequest(dataRequestId);
            const disclosureApiCall = _disclosureService.getDisclosure(disclosureId);
            forkJoin([disclosureApiCall, dataRequestApiCall]).subscribe({
                next: ([loadedDisclosure, loadedDataRequest]) => {
                    setDisclosure(loadedDisclosure);
                    setDataRequest(loadedDataRequest);
                }
            });
        } else {
            setNotFound(true);
        }
    };

    const reviewDocsUri = useMemo(
        () => `${_config.secureApiUri}api/files/${disclosure?.disclosureDocumentPackageId}/true`,
        [_config, disclosure?.disclosureDocumentPackageId]
    );

    const attachmentsUri = useMemo(
        () => `${_config.secureApiUri}api/files/${disclosure?.disclosureAttachmentPackageId}/true`,
        [_config, disclosure?.disclosureAttachmentPackageId]
    );

    useEffect(() => {
        initializeData();
    }, []);

    if (notFound) {
        return <NotFoundComponent />;
    }

    if (!disclosure || !dataRequest) {
        return <LoadIndicatorCenteredView />;
    }

    return (
        <CitationsContextWrapper
            objectId={dataRequestId}
            doiStem={dataRequest.doiStem}
            isStudy={false}
            doUpdate={false}
            disclosureId={disclosure?.id?.toString()}
            externalCitations={disclosure?.citations}
        >
            <DisclosureView
                disclosure={disclosure}
                dataRequest={dataRequest}
                acknowledgementText={
                    'This [publication or presentation, as applicable] is based on research using data from data contributors *Data Contributor(s) Name* that has been made available through Vivli, Inc. Vivli has not contributed to or approved, and is not in any way responsible for, the contents of this publication.'
                }
                documentsForFeedbackUri={attachmentsUri}
                documentsForReviewUri={reviewDocsUri}
                user={user}
                onSubmit={handleSubmit}
                onSave={handleSave}
                onReviewFinalized={handleReviewFinalized}
                onChangeStatus={handleChangeStatus}
                isLoading={isLoading}
                refreshApiToken={_auth.getTokenPopup}
                onLinkClick={(link) => handleLinkClick(link, disclosure?.id)}
                defaultTab={tab}
                attachmentsPackageApi={attachmentsPackage}
                reviewDocsPackageApi={reviewDocsPackage}
                organizations={organizations}
            />
        </CitationsContextWrapper>
    );
};
